import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyProfile } from '../services/api';
import Navbar from '../components/Navbar';


function Section({ title, children }) {
  return (
    <div className="a-card" style={{ marginBottom: '1.25rem' }}>
      <div className="a-section-title">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
      <span style={{ color: 'var(--muted)', minWidth: '160px', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

function ProfileViewPage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    getMyProfile()
      .then(res => setProfile(res.data))
      .catch(() => setError('Failed to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <>
      <Navbar />
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>
    </>
  );

  if (error) return (
    <>
      <Navbar />
      <div style={{ padding: '2rem' }}><div className="a-error">{error}</div></div>
    </>
  );

  const p = profile;
  const hasPhoto = p.profile_photo_url;

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
              {p.stage_name || p.real_name || 'My Profile'}
            </h1>
            {p.stage_name && p.real_name && (
              <p style={{ margin: '0.25rem 0 0', color: 'var(--muted)', fontSize: '0.875rem' }}>{p.real_name}</p>
            )}
          </div>
          <Link to="/profile/edit" className="a-btn a-btn--navy a-btn--sm">Edit Profile</Link>
        </div>

        {/* Status banner */}
        {p.status === 'new' && (
          <div className="a-info" style={{ marginBottom: '1.25rem' }}>
            Your profile is pending review. We'll be in touch once it's been assessed.
          </div>
        )}

        {/* About */}
        <Section title="About">
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0 }}>
              {hasPhoto ? (
                <img src={p.profile_photo_url} alt="Profile" className="a-photo" style={{ width: '96px', height: '96px' }} />
              ) : (
                <div className="a-photo-placeholder" style={{ width: '96px', height: '96px', fontSize: '2rem' }}>
                  {(p.stage_name || p.real_name || '?')[0].toUpperCase()}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              {p.bio ? (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.6 }}>{p.bio}</p>
              ) : (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: 'var(--muted)', fontStyle: 'italic' }}>No bio added yet.</p>
              )}
              <Row label="Phone" value={p.phone} />
              <Row label="Email" value={p.email} />
            </div>
          </div>
        </Section>

        {/* Your Act */}
        <Section title="Your Act">
          <Row label="Performance Type" value={p.performance_type} />
          {p.genres && p.genres.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              {p.genres.map(g => (
                <span key={g.genre_id} className="a-tag">{g.name}</span>
              ))}
            </div>
          )}
          {(p.set_length_min || p.set_length_max) && (
            <Row label="Set Length" value={
              p.set_length_min && p.set_length_max
                ? `${p.set_length_min}–${p.set_length_max} minutes`
                : p.set_length_min
                  ? `From ${p.set_length_min} minutes`
                  : `Up to ${p.set_length_max} minutes`
            } />
          )}
          {p.website && <Row label="Website" value={<a href={p.website} target="_blank" rel="noreferrer">{p.website}</a>} />}
          {p.social_facebook  && <Row label="Facebook"  value={<a href={p.social_facebook}  target="_blank" rel="noreferrer">{p.social_facebook}</a>}  />}
          {p.social_instagram && <Row label="Instagram" value={<a href={p.social_instagram} target="_blank" rel="noreferrer">{p.social_instagram}</a>} />}
          {p.social_youtube   && <Row label="YouTube"   value={<a href={p.social_youtube}   target="_blank" rel="noreferrer">{p.social_youtube}</a>}   />}
          {p.social_tiktok    && <Row label="TikTok"    value={<a href={p.social_tiktok}    target="_blank" rel="noreferrer">{p.social_tiktok}</a>}    />}
          {p.social_spotify   && <Row label="Spotify"   value={<a href={p.social_spotify}   target="_blank" rel="noreferrer">{p.social_spotify}</a>}   />}
        </Section>

        {/* Media */}
        {p.media && p.media.length > 0 && (
          <Section title="Music & Media">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {p.media.map((m, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.9rem' }}>
                  <a href={m.url} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>
                    {m.title || m.url}
                  </a>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Availability */}
        <Section title="Availability">
          {(() => {
            const avail = p.availability || [];
            const notes = p.availability_notes;
            const fmtDMY = (d) => {
              if (!d) return '';
              const dt = new Date(d);
              return `${String(dt.getUTCDate()).padStart(2, '0')}/${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${dt.getUTCFullYear()}`;
            };
            if (avail.length === 0 && !notes) {
              return <p style={{ fontSize: '0.9rem', color: 'var(--muted)', fontStyle: 'italic', margin: 0 }}>Not specified</p>;
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {avail.map((a, i) => {
                  if (a.availability_type === 'always') {
                    return <div key={i} style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Usually available</div>;
                  }
                  if (a.availability_type === 'range') {
                    return (
                      <div key={i} style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                        Available {fmtDMY(a.start_date)} to {fmtDMY(a.end_date)}
                      </div>
                    );
                  }
                  if (a.availability_type === 'specific') {
                    return (
                      <div key={i} style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                        Available on {fmtDMY(a.start_date)}
                      </div>
                    );
                  }
                  return null;
                })}
                {notes && (
                  <div style={{ fontSize: '0.9rem', color: 'var(--muted)', marginTop: '0.25rem' }}>{notes}</div>
                )}
              </div>
            );
          })()}
        </Section>

        {/* Stage Requirements */}
        <Section title="Stage Requirements">
          {(() => {
            const vocalMics = p.stage?.vocal_mics;
            const hasVocalMics = vocalMics != null && vocalMics > 0;
            const hasInstruments = p.instruments && p.instruments.length > 0;
            if (!hasVocalMics && !hasInstruments) {
              return <p style={{ fontSize: '0.9rem', color: 'var(--muted)', fontStyle: 'italic', margin: 0 }}>No stage requirements specified</p>;
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {hasVocalMics && (
                  <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Vocal microphones: {vocalMics}</div>
                )}
                {hasInstruments && p.instruments.map((inst, i) => {
                  const detail = inst.drum_type || inst.amplification;
                  return (
                    <div key={i} style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                      {inst.instrument_name}{detail ? ` — ${detail}` : ''}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Section>

        {/* SCALA Membership */}
        <Section title="SCALA Membership">
          <Row label="Status" value={p.is_scala_member ? 'SCALA member' : 'Not a SCALA member'} />
        </Section>

      </div>
    </>
  );
}

export default ProfileViewPage;
